import React, { useState } from 'react';
import { message } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { nullSafeGet, stopDefaultEvents } from '../../../utils/DataAccessUtils';
import { getWorkOrderLink } from '../../../utils/WorkOrderUtils';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
	dismissWorkOrderNoteStatusForBuyer,
	dismissWorkOrderNoteStatusForSupplier,
} from '../../../thunks/work_orders_thunks';
import { useNoteSender, useMentionSearch } from '../../../hooks/useNoteSending';
import RowNotePreview from '../../row_note_preview/RowNotePreview';
import { useNotesBroadcast } from '../../../hooks/useNotesBroadcast';
import { WEB_SOCKET_ENTITY_TYPES } from '../../../utils/WebSocketUtils';

interface WorkOrderRowNotePreviewProps {
	workOrder: any;
	userType: string;
	targetCollectionName: string | null;
	refreshTable?: (location?) => void;
	refreshWOFromSearch: (id: number) => void;
	dismissNoteStatus: (params: {
		workOrderId: number;
		noteText: string;
		noteAddedAtMillis: number;
	}) => Promise<any>;
}

const WorkOrderRowNotePreview: React.FC<WorkOrderRowNotePreviewProps> = ({
	workOrder,
	userType,
	targetCollectionName,
	refreshTable,
	refreshWOFromSearch,
	dismissNoteStatus,
}) => {
	const location = useLocation();
	const history = useHistory();
	const { sendNote, currentUser, workOrderId } = useNoteSender(workOrder);
	const { onSearch } = useMentionSearch(workOrder);
	const { onSendNote, onTyping } = useNotesBroadcast({
		entityIdName: 'workOrderId',
		entityId: workOrderId,
		entityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_NOTES,
		newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER,
		currentUser,
	});

	const [dismissing, setDismissing] = useState(false);

	const handleDismissNoteStatus = (e) => {
		stopDefaultEvents(e);
		const woId = parseInt(nullSafeGet('id', workOrder), 10);
		const noteText = nullSafeGet('lastNote', workOrder);
		const noteAddedAtMillis = nullSafeGet('lastNoteAddedAt', workOrder);
		setDismissing(true);
		dismissNoteStatus({ workOrderId: woId, noteText, noteAddedAtMillis })
			.then(() => {
				refreshWOFromSearch(woId);
				const hasNoteFilter = location.search && location.search.indexOf('noteStatus') !== -1;
				if (hasNoteFilter && refreshTable) {
					refreshTable(location);
				}
			})
			.catch(() => {
				message.error('Failed to dismiss note status. Please try again.');
			})
			.finally(() => {
				setDismissing(false);
			});
	};

	return (
		<RowNotePreview
			lastNote={nullSafeGet('lastNote', workOrder)}
			lastNoteAddedBy={nullSafeGet('lastNoteAddedBy', workOrder)}
			lastNoteAddedAt={nullSafeGet('lastNoteAddedAt', workOrder)}
			isLastNoteUnread={!!nullSafeGet('isLastNoteUnreadByCurrentUser', workOrder)}
			isLastNoteAwaitingResponse={!!nullSafeGet('isLastNoteAwaitingResponse', workOrder)}
			isLastNoteAwaitingResponseFromCurrentUser={
				!!nullSafeGet('isLastNoteAwaitingResponseFromCurrentUser', workOrder)
			}
			isLastNoteResponsePending={!!nullSafeGet('isLastNoteResponsePending', workOrder)}
			lastNoteTaggedUsers={nullSafeGet('lastNoteTaggedUsers', workOrder)}
			sendNote={sendNote}
			onMentionSearch={onSearch}
			currentUser={currentUser}
			entityId={workOrderId}
			entityLabel={`WO #${workOrderId}`}
			onWsSendNote={onSendNote}
			onWsTyping={onTyping}
			onNoteClick={() => {
				history.push(
					getWorkOrderLink({
						userType,
						tab: 'notes',
						workOrder,
						targetCollectionName,
						location,
						BACK_TEXT: 'Back to Work Orders',
					})
				);
			}}
			onNoteSent={() => {
				const id = parseInt(nullSafeGet('id', workOrder), 10);
				if (id) refreshWOFromSearch(id);
			}}
			onDismissNoteStatus={handleDismissNoteStatus}
			dismissing={dismissing}
		/>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	refreshWOFromSearch: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.read({ id })
				: workOrdersRestCrudThunksForBuyer.read({ id })
		),
	dismissNoteStatus: (params) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? dismissWorkOrderNoteStatusForSupplier(params)
				: dismissWorkOrderNoteStatusForBuyer(params)
		),
});

export default connect(null, mapDispatchToProps)(WorkOrderRowNotePreview as any);
